import dayjs from 'dayjs';
import {invariant} from 'es-toolkit';
import {makeAutoObservable, observable, reaction, runInAction} from 'mobx';

import {delay} from '@yourcoach/async-utils';
import {i18n} from '@yourcoach/i18n';

import type {ConferenceExpandedCommon} from '../../../api/conference';
import type {CoreClientUsersReadParams} from '../../../api/types/core/methods';
import type {ApiReadResponseExpanded, Expand} from '../../../api/types/types';
import type {
  Destructible,
  RequireInitialLoad,
  WithBusyManager,
  WithReactionDispose,
} from '../../../utils/store';
import type {Nullable} from '../../../utils/utility-types';
import type {Status} from '../types';
import type {
  Config,
  OTSessionRef,
  OTStream,
  Participant,
  Signal,
  Stream,
  StreamActivity,
} from './types';

import {conferenceExpandCommon, conferenceStore} from '../../../api/conference';
import {eventStore} from '../../../api/event';
import {currentUserStore, type UserApiModel} from '../../../api/user';
import {apiRequest} from '../../../api/utils/apiRequest';
import {expandObj} from '../../../api/utils/expand';
import {alert} from '../../../components/alert';
import {conferenceFetch} from '../../../services/entity-action/conference/conference.fetch';
import {conferenceJoin} from '../../../services/entity-action/conference/conference.join';
import {conferenceStart} from '../../../services/entity-action/conference/conference.start';
import {conferenceStop} from '../../../services/entity-action/conference/conference.stop';
import {isCancelCommand} from '../../../services/entity-action/utils';
import {not} from '../../../utils/conditionalUtils';
import {datetimeObjToISOString} from '../../../utils/datetime';
import {
  checkDevicePermissions,
  DevicePermission,
  requestMediaPermissions,
} from '../../../utils/device-permissions';
import {
  getConferenceTitle,
  isConferenceCorporate,
  isConferenceCreatedByClient,
  isConferenceEnded,
  isConferenceExpired,
  isConferenceStarted,
  isConferenceStartEnabled,
  isMeConferenceHost,
} from '../../../utils/entity/conference';
import {getFileSrc} from '../../../utils/entity/file';
import {getEntityTypeById} from '../../../utils/entity/getEntityType';
import {isMeCoachForEntity} from '../../../utils/entity/isMeCoachForEntity';
import {envManager} from '../../../utils/env';
import {AppError} from '../../../utils/errors/app';
import {emitter, WS_RECEIVE_MESSAGE_EVENT} from '../../../utils/eventEmitter';
import {logger} from '../../../utils/logger';
import {platform} from '../../../utils/platform';
import {
  AsyncBusyManager,
  InitialLoadManager,
  ReactionDisposeManager,
} from '../../../utils/store';
import {isDefined} from '../../../utils/typecheck';
import {showAccessToMediaDevicesDeniedAlert} from '../alerts/showAccessToMediaDevicesDeniedAlert';
import {logConferenceActivity} from '../utils';
import {conferenceStopHandler} from './ConferenceStopHandler.store';
import {RecordingManager} from './RecordingManager';
import {showBusyAlert} from './showBusyAlert';
import {
  showRecordConfirm,
  type ShowRecordConfirmParams,
} from './showRecordConfirm';
import {ConferenceStats} from './Stats.store';
import {
  BUSY_ALERT_SHOW_TIME_IN_MINS,
  FORCE_DISCONNECT_SIGNAL,
  LIVE_CONF_AUTO_CLOSE_DELAY_IN_SEC,
  MAX_CONNECTIONS,
  PUBLISHER_STREAM_ID,
  RECORDING_REFUSED_SIGNAL,
} from './utils';

type ReasonKey = 'fetch' | 'stop';

// TODO: Split this store into multiple smaller stores
export class ConferenceStore
  implements
    Destructible,
    RequireInitialLoad,
    WithBusyManager<ReasonKey>,
    WithReactionDispose
{
  static PUBLISHER_STREAM_ID = 'publisher_stream';
  readonly busy = new AsyncBusyManager<ReasonKey>();
  // for mobile
  camera: 'back' | 'front' = 'front';
  conference: ConferenceExpandedCommon | null = null;
  conferenceId: string;
  disposer = new ReactionDisposeManager();
  initialLoad = new InitialLoadManager();
  isAudioEnabled = true;
  isAwaitingForHost = false;
  isJoinBeforeHostAllowed = true;
  isReceivingRemoteStreams = true;
  isVideoEnabled = true;
  mode: 'gallery' | 'grid' = 'grid';
  otStreams = new Map<string, OTStream>();
  recordingManager = new RecordingManager();
  session: OTSessionRef;
  sessionId = '';
  speakerStream: null | Stream = null;
  stats: ConferenceStats;
  status: Status = 'connecting';
  streams = observable.map<string, Stream>();
  token = '';
  private busyAlertTimeout = 0;
  private closeDelay = LIVE_CONF_AUTO_CLOSE_DELAY_IN_SEC;
  private closeDelayInterval = 0;
  private goBack: () => void;
  private isConferenceStartedWsMessageHandled = false;
  private lastAudioToggleAt = 0;
  private lastVideoToggleAt = 0;
  private readonly onOpponentJoined: Config['onOpponentJoined'];
  private publisherStreamActivity: StreamActivity = {
    isTalking: false,
    timestamp: Date.now(),
  };
  private publisherStreamCreationTime = Date.now();
  private publisherStreamIsTalking = false;
  private readonly shouldRequestMediaPermissions: boolean;
  private startTime: Nullable<number> = null;
  private streamsActivity = new Map<string, StreamActivity>();

  constructor({
    conferenceId,
    goBack,
    isAudioEnabled,
    isJoinBeforeHostAllowed,
    isVideoEnabled,
    onOpponentJoined,
    shouldRequestMediaPermissions,
  }: Config) {
    this.conferenceId = conferenceId;
    this.goBack = goBack;
    this.onOpponentJoined = onOpponentJoined;
    this.shouldRequestMediaPermissions = shouldRequestMediaPermissions ?? false;

    this.isAudioEnabled = isDefined(isAudioEnabled)
      ? isAudioEnabled
      : this.isAudioEnabled;
    this.isVideoEnabled = isDefined(isVideoEnabled)
      ? isVideoEnabled
      : this.isVideoEnabled;

    logConferenceActivity('ConferenceStore construct', conferenceId);
    logConferenceActivity('is audio enabled initially', this.isAudioEnabled);
    logConferenceActivity('is video enabled initially', this.isVideoEnabled);

    this.isJoinBeforeHostAllowed = isJoinBeforeHostAllowed || true;

    this.stats = new ConferenceStats({
      isVideoEnabled: this.isVideoEnabled,
    });

    this.busy.observe<ConferenceStore, 'fetchConference'>(this, {
      fetchConference: 'fetch',
      stop: 'stop',
    });

    makeAutoObservable<ConferenceStore, 'onOpponentJoined'>(
      this,
      {
        onOpponentJoined: false,
        otStreams: false,
      },
      {autoBind: true},
    );

    logger.event('video_screen_view');
  }

  init() {
    this.initialLoad.doneWhen(() => !!this.conference);

    this.setupReactions();

    this.fetchConference();

    emitter.on(WS_RECEIVE_MESSAGE_EVENT, this.onWsMessage);
  }

  destruct() {
    this.disposer.dispose();
    clearInterval(this.closeDelayInterval);
    this.clearBusyAlertTimeout();

    emitter.off(WS_RECEIVE_MESSAGE_EVENT, this.onWsMessage);
  }

  get avatar() {
    return getFileSrc(this.conference?.course?.program.avatar, 250);
  }

  get hostStream() {
    const hostStream = Array.from(this.streams.values()).find(
      stream => stream.isHost,
    );

    logConferenceActivity('host stream', hostStream);

    return hostStream;
  }

  get isAbleConnect() {
    const isAbleConnect = !!(this.conference && this.sessionId && this.token);

    logConferenceActivity('is able to connect', isAbleConnect);

    return isAbleConnect;
  }

  get isClientConference() {
    return isConferenceCreatedByClient(this.conference);
  }

  get isFetching() {
    return this.busy.getReason('fetch');
  }

  /**
   * Means that is stream from publisher.
   * For use in gallery mode to show copy of publisher
   */
  get myStream() {
    const myStream =
      Array.from(this.streams.values()).find(stream => stream.isMy) || null;

    logConferenceActivity('my stream', myStream);

    return myStream;
  }

  get participantName() {
    if (!this.conference) {
      return '';
    }

    const {client, coaches, course} = this.conference;

    if (this.isClientConference && coaches.length && client) {
      if (isMeCoachForEntity(this.conference)) {
        return client.name;
      }

      return coaches[0]!.name;
    } else if (course?.client_id && isMeCoachForEntity(this.conference)) {
      return course.client.name;
    }

    return '';
  }

  get participants() {
    const participants: Participant[] = Array.from(this.streams.values())
      .concat(this.publisherStream)
      .filter(stream => !stream.isMy)
      .map(stream => ({
        _id: stream.userId,
        avatar: stream.avatar,
        name: stream.name,
        stream_id: stream.streamId,
      }));

    return participants;
  }

  get participantsCount() {
    return this.participants.length;
  }

  get programTitle() {
    return this.conference?.course?.program.title;
  }

  get publisherStream() {
    // @ts-expect-error: TODO: describe why
    const stream: Stream = {
      avatar: getFileSrc(currentUserStore.user?.avatar).url,
      creationTime: this.publisherStreamCreationTime,
      hasAudio: this.isAudioEnabled,
      hasVideo: this.isVideoEnabled,
      isAudioBlocked: false,
      isCoach: isMeCoachForEntity(this.conference),
      isHost: isMeConferenceHost(this.conference),
      isPublisher: true,
      isTalking: this.publisherStreamIsTalking,
      isVideoDisabled: !this.isVideoEnabled,
      name: currentUserStore.user?.name || '',
      streamId: PUBLISHER_STREAM_ID,
      userId: currentUserStore.user?._id || '',
      videoType: 'camera',
    };

    logConferenceActivity('publisher stream', stream);

    return stream;
  }

  get stopButtonLabel() {
    let label = i18n.t('shared:disconnect');

    if (isMeConferenceHost(this.conference)) {
      label = i18n.t('shared:Conference.stop_button');
    }

    if (this.isAwaitingForHost) {
      label = i18n.t('shared:close');
    }

    return label;
  }

  get streamsArray() {
    const streams: Stream[] = [];

    this.streams.forEach(stream => {
      // we need to clone the stream object to make getter react on changes
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const trigger = {...stream};

      if (!stream.isMy) {
        streams.push(stream);
      }
    });

    /*
    // Use this to test layout! Enter the conference as the 1 host and 1 participant
    // @ts-expect-error
    if (streams.length) {
      const fakeStreams: Stream[] = new Array(9)
        .fill(Array.from(this.streams.values())[0])
        .map((stream, i) => ({
          ...stream,
          userId: `user_id:${i}`,
          name: `Stream ${i + 1}`,
          isHost: false,
          isCoach: false,
          isTalking: false,
          hasAudio: true,
          hasVideo: false,
          streamId: `stream:${i}`,
          creationTime: Date.now(),
        }));

      return fakeStreams;
    }
    */

    const sortedStreams = streams.sort((a, b) => {
      if (Number(a.isCoach) < Number(b.isCoach)) {
        return 1;
      } else if (Number(a.isCoach) > Number(b.isCoach)) {
        return -1;
      } else {
        return (
          dayjs(a.creationTime).valueOf() - dayjs(b.creationTime).valueOf()
        );
      }
    });

    logConferenceActivity('streams array', sortedStreams);

    return sortedStreams;
  }

  get title() {
    return getConferenceTitle();
  }

  /**
   * @see https://tokbox.com/developer/guides/customize-ui/js/#active_speaker
   */
  detectSpeaker(streamId: string, audioLevel: number) {
    if (audioLevel < 0) {
      return;
    }

    let stream: Stream | undefined;
    let streamActivity: StreamActivity | undefined;

    if (streamId === PUBLISHER_STREAM_ID) {
      stream = this.publisherStream;
      streamActivity = this.publisherStreamActivity;
    } else {
      stream = this.streams.get(streamId);
      streamActivity = this.streamsActivity.get(streamId);
    }

    if (!stream || !streamActivity) {
      return;
    }

    /**
     * Vonage video SDK sends audioLevelUpdate event even if a stream has muted
     * so we remove the taking frame on it
     */
    if (!stream.hasAudio) {
      if (streamActivity.isTalking) {
        this.setStreamActivity(streamId, {
          ...streamActivity,
          isTalking: false,
        });
      }

      return;
    }

    const now = Date.now();

    if (audioLevel > 0.1) {
      if (streamActivity.isTalking) {
        this.setStreamActivity(streamId, {...streamActivity, timestamp: now});
      } else if (now - streamActivity.timestamp > 1000) {
        // detected audio activity for more than 1s
        // for the first time.
        this.setStreamActivity(streamId, {
          ...streamActivity,
          isTalking: true,
        });

        if (this.speakerStream?.streamId !== stream.streamId) {
          /**
           * set speaker stream for the gallery view
           * if the publisher speaks we set the my stream as a speaker
           */
          this.speakerStream =
            stream.streamId === PUBLISHER_STREAM_ID ? this.myStream : stream;
        }
      }
    } else if (now - streamActivity.timestamp > 2000) {
      // detected low audio activity for more than 2s
      if (streamActivity.isTalking) {
        this.setStreamActivity(streamId, {
          ...streamActivity,
          isTalking: false,
        });
      }
    }
  }

  getPublisherStreamName() {
    return this.makeStreamName(
      currentUserStore.user?.name || '',
      currentUserStore.user?._id || '',
      true,
    );
  }

  handlePublisherError(error: any) {
    let title = i18n.t('shared:message.error');
    let message = error.message;

    logConferenceActivity('publisher error', error);

    // for web we should use error.name but for mobile error.code
    const errorCode = error.name || error.code;

    // @see for web https://tokbox.com/developer/sdks/js/reference/Error.html
    // @see for android https://tokbox.com/developer/sdks/android/reference/com/opentok/android/OpentokError.ErrorCode.html
    // @see for ios https://tokbox.com/developer/sdks/ios/reference/Constants/OTPublisherErrorCode.html
    switch (errorCode) {
      case 'ConnectionDropped':
      case 'ConnectionFailed':
      case 'ConnectionTimedOut':
      case 'OT_MEDIA_ERR_NETWORK':
      case 'OT_TIMEOUT':
      case 'OTSessionDisconnected':
      case 'PublisherTimeout':
        title = i18n.t('shared:poor_network_connection');
        message = i18n.t('shared:conference_poor_connection');
        break;
      case 'OT_HARDWARE_UNAVAILABLE':
        title = i18n.t('shared:unable_to_access_media');
        message = i18n.t('shared:conference_unable_to_access_media');
        break;
      case 'OT_NOT_CONNECTED':
        message = i18n.t('shared:Conference.not_connected_error');
        break;
      default:
        break;
    }

    message += ` (${errorCode || 'UNKNOWN_ERROR'})`;

    logger.event('video_loaded_fail', {reason: error.message});

    alert.show({
      buttons: [
        {
          label: i18n.t('shared:ok'),
        },
      ],
      message,
      title,
    });

    logger.error(error);
  }

  handleSessionError(error: any) {
    logger.event('video_loaded_fail', {reason: error.message});

    logConferenceActivity('session error', error);

    alert.show({
      buttons: [
        {
          label: i18n.t('shared:ok'),
          onClick: this.goBack,
        },
      ],
      message: error.message,
      title: i18n.t('shared:message.error'),
    });

    logger.error(error);
  }

  handleSignal(signal: Signal) {
    const signalType = signal.type?.replace('signal:', '');
    const data = signal.data;

    switch (signalType) {
      case FORCE_DISCONNECT_SIGNAL:
        this.handleForceDisconnectSignal();
        break;
      case RECORDING_REFUSED_SIGNAL:
        this.handleRecordingRefusedSignal(data);
        break;
      default:
        break;
    }
  }

  async joinBeforeHostNotify() {
    try {
      if (!this.conference) {
        throw new AppError('conference is not defined');
      }

      if (isConferenceStarted(this.conference)) {
        return;
      }

      // if the conference isn't started yet,
      // the backend will send the `conference_client_join` event
      await conferenceJoin({
        entity: this.conference,
        params: {
          expand: conferenceExpandCommon,
        },
      });

      this.scheduleShowBusyAlert();
    } catch (error) {
      logger.error(error);
    }
  }

  onRecordStarted() {
    this.showRecordConfirm({
      hasAudio: this.isAudioEnabled,
      hasVideo: this.isVideoEnabled,
    });

    this.recordingManager.setIsRecording(true);
  }

  onRecordStopped() {
    this.recordingManager.setIsRecording(false);
  }

  onSessionConnected() {
    logger.event('video_loaded_success');

    logConferenceActivity('session connected', '');

    this.startTime = Date.now();
  }

  async onStreamCreated(
    stream: OTStream,
    {isMy}: {isMy: boolean} = {isMy: false},
  ) {
    logConferenceActivity('stream created', stream, 'is my', isMy);

    if (isMy) {
      this.onPublisherStreamCreated(stream);

      return;
    }

    invariant(
      stream.connection?.connectionId,
      'stream connection is not defined',
    );

    const {streamId} = stream;

    this.otStreams.set(streamId, stream);

    if (this.streams.has(streamId)) {
      logConferenceActivity('stream already exists', stream);

      return;
    }

    this.autoStartRecordIfNeeded();

    const {id, isPublisher, name} = this.parseStreamName(stream.name);
    const isHost = this.conference?.moderator_id === id;
    const streamToAdd: Stream = {
      ...stream,
      avatar: null,
      isAudioBlocked: false,
      isCoach: !!this.conference?.coach_ids.includes(id),
      isHost,
      isMocked: false,
      isMy,
      isPublisher,
      isTalking: false,
      isVideoDisabled: !stream.hasVideo,
      isVideoDisabledWarning: false,
      name,
      userId: id,
    };

    runInAction(() => {
      this.streams.set(streamId, streamToAdd);
      logConferenceActivity('stream added', streamToAdd);

      if (this.streams.size === 2) this.onOpponentJoined?.();

      this.fetchUserDataForStream(stream.streamId);
      this.streamsActivity.set(stream.streamId, {
        isTalking: false,
        timestamp: Date.now(),
      });

      if (!this.speakerStream) this.speakerStream = streamToAdd;
    });
  }

  onStreamDestroyed(stream: OTStream) {
    const streamId = stream?.streamId;

    if (!streamId) {
      return;
    }

    const targetStream = this.streams.get(streamId);

    this.otStreams.delete(streamId);

    logConferenceActivity('stream destroyed', stream);

    if (targetStream) this.removeStream(streamId);
  }

  onStreamPropertyChanged(
    stream: OTStream,
    changedProperty: string,
    newValue: any,
  ) {
    // sometimes stream may be undefined (from native side)
    if (!stream) {
      return;
    }

    const targetStream = this.streams.get(stream.streamId);

    logConferenceActivity('stream property changed', {
      changedProperty,
      newValue,
      stream,
    });

    if (targetStream) {
      targetStream[changedProperty] = newValue;

      if (changedProperty === 'hasVideo') {
        targetStream.isVideoDisabled = !newValue;
        targetStream.isVideoDisabledWarning = false;
        targetStream.videoDisableReason = undefined;
      }

      if (
        this.speakerStream &&
        targetStream.streamId === this.speakerStream.streamId
      ) {
        this.speakerStream[changedProperty] = newValue;

        if (changedProperty === 'hasVideo') {
          this.speakerStream.isVideoDisabled = !newValue;
          this.speakerStream.isVideoDisabledWarning = false;
          this.speakerStream.videoDisableReason = undefined;
        }
      }
    }
  }

  parseStreamName(streamName: string) {
    const [name, id, isPublisher] = streamName.split('|');

    return {
      id: id || '',
      isPublisher: isPublisher === 'true' ? true : false,
      name: name || '',
    };
  }

  setIsAudioEnabled(value: boolean) {
    this.isAudioEnabled = value;

    logConferenceActivity('set is audio enabled', this.isAudioEnabled);

    logger.event('audio_tap', {
      type: this.isAudioEnabled ? 'unmute' : 'mute',
    });
  }

  setIsReceivingRemoteStreams(value: boolean) {
    this.isReceivingRemoteStreams = value;

    logConferenceActivity(
      'set is receiving remote streams',
      this.isReceivingRemoteStreams,
    );
  }

  setIsVideoEnabled(value: boolean) {
    this.isVideoEnabled = value;

    logger.event('camera_tap', {type: this.isVideoEnabled ? 'on' : 'off'});

    logConferenceActivity('set is video enabled', this.isVideoEnabled);

    this.stats.setIsVideoEnabled(this.isVideoEnabled);
  }

  setSession(session: OTSessionRef) {
    this.session = session;

    logConferenceActivity('session is set', session);
  }

  setStatus(status: Status) {
    if (
      platform.isWeb &&
      this.status === 'connected' &&
      status === 'connecting'
    ) {
      this.pingGoogle();
    }

    this.status = status;
  }

  showRecordConfirm({
    hasAudio,
    hasVideo,
  }: {
    hasAudio: boolean;
    hasVideo: boolean;
  }) {
    if (isMeConferenceHost(this.conference)) {
      return;
    }

    const isCorporate = isConferenceCorporate(this.conference);

    // this is a special case (initially for "circle engage" partner)
    // for such conferences (corporate intro calls) we should apply "b2b widget" logic
    const isCorporateWithoutCourse = isCorporate && !this.conference?.course_id;

    let titleKey: ShowRecordConfirmParams['titleKey'] = 'regular';

    if (envManager.isB2bWidget || isCorporate) {
      titleKey = 'quality_assurance';
    }

    let declineMode: ShowRecordConfirmParams['declineMode'] = 'disconnect';

    if (envManager.isB2bWidget || isCorporateWithoutCourse) {
      declineMode = 'disconnect';
    } else if (envManager.isCore && isCorporate) {
      declineMode = 'do_not_record';
    }

    showRecordConfirm({
      declineMode,
      /**
       * and move audio/video settings back as a user agreed to be recorded
       */
      onAllow: () => {
        logger.event('session_recording_permission_tap', {
          granted: 'yes',
        });

        runInAction(() => {
          this.isAudioEnabled = hasAudio;
          this.isVideoEnabled = hasVideo;
        });
      },
      onDecline: () => {
        logger.event('session_recording_permission_tap', {
          granted: 'no',
        });

        if (declineMode === 'disconnect' && titleKey === 'quality_assurance') {
          this.goBack();

          return;
        }

        if (declineMode === 'do_not_record') {
          // we continue the conference, but we need to notify coach that
          // a b2b client on core refused to be recorded in order to stop the recording
          this.sendSignal({
            data: this.myStream?.streamId,
            type: RECORDING_REFUSED_SIGNAL,
          });

          runInAction(() => {
            this.isAudioEnabled = hasAudio;
            this.isVideoEnabled = hasVideo;
          });

          return;
        }

        this.goBack();
      },
      /**
       * when the alert appears, the conference is already recording
       * we disable audio and video, which removes a user from recording
       */
      onShow: () => {
        logger.event('session_recording_permission_view');

        runInAction(() => {
          this.isAudioEnabled = false;
          this.isVideoEnabled = false;
        });
      },
      titleKey,
    });
  }

  async stop() {
    const prevStatus = this.status;

    try {
      if (!this.conference) {
        throw new AppError('conference is not defined');
      }

      if (this.startTime) {
        const duration = Math.floor((Date.now() - this.startTime) / 1000); // seconds

        logger.event('video_end_tap', {
          duration,
        });
      }

      if (!isMeConferenceHost(this.conference)) {
        if (this.isAwaitingForHost) {
          logger.event('session_preview_close_tap');
        }

        this.goBack();

        return;
      }

      const result = await conferenceStop({
        entity: this.conference,
        params: {
          conference_type:
            this.conference.type === 'phone'
              ? 'phone'
              : this.stats.getConferenceType(),
        },
      });

      if (isCancelCommand(result)) {
        return;
      }

      if (prevStatus === 'connected') {
        this.sendSignal({
          type: FORCE_DISCONNECT_SIGNAL,
        });
        this.goBack();
      } else {
        this.goBack();
      }

      logConferenceActivity('conference stopped', this.conference);

      conferenceStore.coach.localUpdate(this.conference!, {
        session_id: null,
        status: 'past',
      });
    } catch (error) {
      logger.error(error);
      await alert.show({
        message: error.message,
        title: i18n.t('shared:Conference.stop_conference_error_message'),
      });

      this.goBack();
    }
  }

  toggleAudio() {
    const now = Date.now();

    if (now - this.lastAudioToggleAt < 1000) return;

    this.lastAudioToggleAt = now;
    this.isAudioEnabled = !this.isAudioEnabled;

    logConferenceActivity('toggle audio', this.isAudioEnabled);

    logger.event('audio_tap', {
      type: this.isAudioEnabled ? 'unmute' : 'mute',
    });
  }

  toggleCamera() {
    this.camera = this.camera === 'front' ? 'back' : 'front';
  }

  toggleMode() {
    this.mode = this.mode === 'grid' ? 'gallery' : 'grid';
  }

  toggleVideo() {
    const now = Date.now();

    if (now - this.lastVideoToggleAt < 1000) return;

    this.lastVideoToggleAt = now;
    this.isVideoEnabled = !this.isVideoEnabled;

    logger.event('camera_tap', {type: this.isVideoEnabled ? 'on' : 'off'});

    logConferenceActivity('toggle video', this.isVideoEnabled);

    this.stats.setIsVideoEnabled(this.isVideoEnabled);
  }

  private autoStartRecordIfNeeded() {
    if (
      envManager.isCore &&
      isConferenceCorporate(this.conference) &&
      isMeCoachForEntity(this.conference) &&
      isConferenceStarted(this.conference)
    ) {
      this.recordingManager.startRecord();
    }
  }

  private clearBusyAlertTimeout() {
    if (this.busyAlertTimeout) {
      clearTimeout(this.busyAlertTimeout);
      this.busyAlertTimeout = 0;
    }
  }

  private async fetchConference() {
    try {
      const conference = (await conferenceFetch({
        params: {_id: this.conferenceId, expand: conferenceExpandCommon},
      })) as ConferenceExpandedCommon;

      const isMeClient = not(isMeCoachForEntity(conference));

      logConferenceActivity('conference fetched', conference);

      if (isConferenceExpired(conference)) {
        throw new AppError(
          i18n.t('shared:Conference.conference_expired_message'),
        );
      } else if (isConferenceEnded(conference)) {
        throw new AppError(
          i18n.t('shared:Conference.conference_ended_message'),
        );
      } else if (!isConferenceStarted(conference)) {
        if (!isConferenceStartEnabled(conference)) {
          throw new AppError(
            i18n.t('shared:Conference.conference_upcoming_message', {
              date: dayjs(datetimeObjToISOString(conference.start_date)).format(
                'lll',
              ),
            }),
          );
        } else if (isMeClient) {
          if (this.isJoinBeforeHostAllowed) {
            runInAction(() => {
              this.isAwaitingForHost = true;
            });
          } else {
            throw new AppError(
              i18n.t('shared:Conference.conference_not_started_message'),
            );
          }
        }
      }

      runInAction(() => {
        this.conference = conference;
      });

      if (this.isAwaitingForHost) {
        this.joinBeforeHostNotify();
      } else {
        this.initConference(conference);
      }
    } catch (error) {
      logger.error(error);
      await alert.error(error);
      this.goBack();
    }
  }

  private async fetchUserDataForStream(streamId: string) {
    const stream = this.streams.get(streamId);

    if (!stream || stream.isMy) {
      return;
    }

    const expand: Expand = {
      user: ['avatar_id'],
    };

    const userId = stream.userId;

    const isLead = getEntityTypeById(userId) === 'lead';

    if (!userId || isLead) {
      return;
    }

    try {
      const response: ApiReadResponseExpanded<'user', UserApiModel> =
        await apiRequest<CoreClientUsersReadParams>({
          method: 'client.users.read',
          params: {
            _id: stream.userId,
            expand,
          },
        });

      const user = expandObj(response.user, {
        expand: {
          user: ['avatar_id'],
        },
        expanded: response._expanded,
      });

      const avatar = getFileSrc(user.avatar).url;

      runInAction(() => {
        this.streams.set(streamId, {
          ...stream,
          avatar,
          name: user.name,
        });
      });
    } catch (error) {
      logger.error(error);
    }
  }

  private handleForceDisconnectSignal() {
    const isHost = isMeConferenceHost(this.conference);

    if (!isHost) {
      logConferenceActivity('force disconnect signal received');

      let hostName = 'Your Coach';

      const coach = (
        this.conference?.course?.program.expanded_coaches || []
      ).find(
        expandedCoach => expandedCoach._id === this.conference?.moderator_id,
      );

      if (coach) {
        hostName = coach.name;
      }

      alert.show({
        buttons: [
          {
            label: i18n.t('shared:Conference.close_button', {
              timer: this.closeDelay,
            }),
            onClick: () => {
              this.goBack();
            },
          },
        ],
        title: i18n.t('shared:Conference.coach_stopped_conference_message', {
          name: hostName,
        }),
      });

      this.closeDelayInterval = +setInterval(() => {
        if (this.closeDelay > 0) {
          alert.update({
            ...alert.getCurrentAlert(),
            buttons: [
              {
                label: i18n.t('shared:Conference.close_button', {
                  timer: this.closeDelay,
                }),
                onClick: () => {
                  this.goBack();
                },
              },
            ],
          });

          this.closeDelay = this.closeDelay - 1;
        } else {
          clearInterval(this.closeDelayInterval);

          alert.hide();

          this.goBack();
        }
      }, 1000);
    } else {
      this.goBack();
    }
  }

  private handleRecordingRefusedSignal(_streamId?: string) {
    const isHost = isMeConferenceHost(this.conference);

    if (!isHost) {
      return;
    }

    logConferenceActivity('recording refused signal received');

    // just to double check that the conference is corporate
    if (envManager.isCore && isConferenceCorporate(this.conference)) {
      this.recordingManager.stopRecord();
    }
  }

  private async initConference(conference: ConferenceExpandedCommon) {
    eventStore.markReadAll({
      limit: 500,
      query: [['context_ids', 'in', [this.conferenceId]]],
    });

    if (this.shouldRequestMediaPermissions) {
      await requestMediaPermissions([
        DevicePermission.CAMERA,
        DevicePermission.MICROPHONE,
      ]);
    }

    const devicePermissions = await checkDevicePermissions([
      DevicePermission.CAMERA,
      DevicePermission.MICROPHONE,
    ]);

    if (!devicePermissions.hasAllPermissions) {
      await showAccessToMediaDevicesDeniedAlert();

      this.goBack();
    } else {
      if (isConferenceStarted(conference)) {
        this.join();
      } else if (isMeCoachForEntity(conference)) {
        this.start();
      }

      if (this.isAwaitingForHost) {
        this.isAwaitingForHost = false;
      }
    }
  }

  private async join() {
    try {
      if (!this.conference) {
        throw new AppError('conference is not defined');
      }

      const result = await conferenceJoin({
        entity: this.conference,
        params: {
          expand: conferenceExpandCommon,
        },
      });

      if (result) {
        runInAction(() => {
          this.conference = result.conference as ConferenceExpandedCommon;
          this.sessionId = result.conference.session_id!;
          this.token = result.token;
          this.setStatus('connecting');

          logConferenceActivity('conference joined', this.conference);
        });
      }

      conferenceStopHandler.setJoinedConference(this.conference);
    } catch (error) {
      logger.error(error);
      await alert.show({
        message: error.message,
        title: i18n.t('shared:Conference.join_conference_error_message'),
      });

      this.goBack();
    }
  }

  private makeStreamName(
    userName: string,
    userId: string,
    isPublisher = false,
  ) {
    return [userName, userId, isPublisher].join('|');
  }

  private onOffline() {
    alert.show({
      message: i18n.t('shared:Conference.not_connected_error'),
      title: i18n.t('shared:message.error'),
    });
  }

  private onPublisherStreamCreated(stream: OTStream) {
    const publisherStream = this.publisherStream;
    const streamId = stream.streamId || publisherStream.streamId;

    this.otStreams.set(streamId, stream);

    if (this.streams.has(streamId)) {
      logConferenceActivity('stream already exists', stream);

      return;
    }

    this.stats.setStartTime(Date.now());

    this.clearBusyAlertTimeout();

    const streamToAdd = {
      ...publisherStream,
      ...stream,
      avatar: publisherStream.avatar,
      creationTime: stream.creationTime || publisherStream.creationTime,
      hasAudio: publisherStream.hasAudio,
      hasVideo: publisherStream.hasVideo,
      isAudioBlocked: false,
      isCoach: publisherStream.isCoach,
      isHost: publisherStream.isHost,
      isMocked: false,
      isMy: true,
      isPublisher: true,
      isTalking: publisherStream.isTalking,
      isVideoDisabled: publisherStream.isVideoDisabled,
      isVideoDisabledWarning: false,
      name: publisherStream.name,
      streamId,
      userId: publisherStream.userId,
      videoType: stream.videoType || publisherStream.videoType,
    } as Stream;

    runInAction(() => {
      this.streams.set(streamId, streamToAdd);

      logConferenceActivity('publisher stream added', streamToAdd);

      this.streamsActivity.set(streamId, {
        isTalking: false,
        timestamp: Date.now(),
      });

      if (!this.speakerStream) {
        this.speakerStream = streamToAdd;
      }
    });
  }

  private onWsMessage(message: any) {
    if (
      message.event.type === 'conference_started' &&
      message.conference?._id === this.conference?._id
    ) {
      // b2b backend sends the conference_started event twice
      if (this.isConferenceStartedWsMessageHandled) {
        return;
      }

      this.isConferenceStartedWsMessageHandled = true;

      if (this.isAwaitingForHost) {
        runInAction(async () => {
          // as the backend sends the limited conference object
          // we should set the needed fields to mark the conference as started
          this.conference = {
            ...this.conference!,
            // will be overwritten by start/join methods
            session_id: 'session_id',
            status: 'ongoing',
          };

          // TODO: backend should not throw the 500 error, then we can get rid of the delay
          // wait for the conference to be started on the backend
          await delay(2000);

          this.initConference(this.conference);
        });
      }
    }
  }

  private async pingGoogle() {
    try {
      await fetch('https://google.com', {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
        method: 'HEAD',
        mode: 'no-cors',
      });
    } catch (e) {
      this.onOffline();
    }
  }

  private removeStream(streamId: string) {
    const targetStream = this.streams.get(streamId);

    if (targetStream) {
      this.streams.delete(targetStream.streamId);

      if (targetStream.streamId === this.speakerStream?.streamId) {
        this.speakerStream = this.streamsArray.length
          ? this.hostStream || this.myStream || this.streamsArray[0]!
          : null;
      }

      this.streamsActivity.delete(targetStream.streamId);
    }
  }

  private scheduleShowBusyAlert() {
    const shouldShowAlert =
      !isMeConferenceHost(this.conference) &&
      currentUserStore.isCrmUser &&
      !isConferenceStarted(this.conference);

    if (!shouldShowAlert) {
      return;
    }

    // show alert after 15 mins of inactivity from conference start date
    const startDate = dayjs(
      datetimeObjToISOString(this.conference?.start_date),
    );
    const alertShowTime = startDate.add(
      BUSY_ALERT_SHOW_TIME_IN_MINS,
      'minutes',
    );
    const timeoutSeconds = alertShowTime.diff(dayjs(), 'seconds');

    this.busyAlertTimeout = +setTimeout(() => {
      showBusyAlert();
    }, timeoutSeconds * 1000);
  }

  private sendSignal(signal: Signal) {
    try {
      logConferenceActivity('signal sent', signal.type, signal.data);

      this.session.sendSignal(signal);
    } catch (error) {
      logger.error(error);
    }
  }

  private setStreamActivity(streamId: string, activity: StreamActivity) {
    if (streamId === PUBLISHER_STREAM_ID) {
      this.publisherStreamActivity = activity;

      if (activity.isTalking !== this.publisherStreamIsTalking) {
        this.publisherStreamIsTalking = activity.isTalking;
      }
    } else {
      const stream = this.streams.get(streamId);
      const streamCurrentActivity = this.streamsActivity.get(streamId);

      if (stream) {
        if (streamCurrentActivity?.isTalking !== activity.isTalking) {
          this.streams.set(stream.streamId, {
            ...stream,
            isTalking: activity.isTalking,
          });
        }

        this.streamsActivity.set(streamId, activity);
      }
    }
  }

  private setupReactions() {
    this.disposer.watch(
      'max_connections',
      reaction(
        () => this.streams.size,
        () => {
          const myStreams = Array.from(this.streams.values()).filter(
            stream => stream.userId === currentUserStore.user?._id,
          );

          if (myStreams.length > MAX_CONNECTIONS) {
            this.session.disconnect();

            alert.show({
              buttons: [
                {
                  label: i18n.t('shared:ok'),
                  onClick: () => {
                    this.goBack();
                  },
                },
              ],
              message: i18n.t('shared:Conference.max_connections_message', {
                devicesCount: myStreams.length,
              }),
              title: i18n.t('shared:message.error'),
            });
          }
        },
      ),
    );

    this.disposer.watch(
      'conference',
      reaction(
        () => this.conference,
        conference => {
          if (conference) this.recordingManager.setConference(conference);
        },
      ),
    );

    this.disposer.watch(
      'conference_recording_check',
      reaction(
        () => this.conference,
        (conference, prevConference) => {
          // run on the first set of the conference
          if (conference && !prevConference) {
            const isRecording = conference.is_recording;

            this.recordingManager.setIsRecording(Boolean(isRecording));
          }
        },
      ),
    );
  }

  private async start() {
    try {
      invariant(this.conference, 'conference is not defined');

      const shouldTakeConference =
        !this.conference.speaker_ids.length &&
        this.conference.billing_type !== 'crm_call';

      if (shouldTakeConference) {
        await conferenceStore.coach.take(this.conference);
      }

      const result = await conferenceStart.asCoach({
        entity: this.conference,
        params: {
          expand: conferenceExpandCommon,
        },
      });

      if (result) {
        runInAction(() => {
          this.conference = result.conference as ConferenceExpandedCommon;
          this.sessionId = result.conference.session_id!;
          this.token = result.token;
          this.setStatus('connecting');

          logConferenceActivity('conference started', this.conference);
        });
      }

      conferenceStopHandler.setJoinedConference(this.conference);
    } catch (error) {
      logger.error(error);
      await alert.show({
        message: error.message,
        title: i18n.t('shared:Conference.start_conference_error_message'),
      });

      this.goBack();
    }
  }
}
